import {
  UserAssignmentType,
  UserErrorCode,
  useUserCreateMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { AlreadyExistingItem } from 'components/alreadyExistingItem';
import { FormOnSubmit, ModalForm } from 'components/form';
import { UserPlusIcon } from 'components/icons';
import React, { ReactElement, useState } from 'react';
import {
  EmailAlreadyExistsErrorExtensions,
  PhoneAlreadyExistsErrorExtensions,
} from 'types';
import { isEmpty, useIsMounted } from 'utils';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import {
  getAddInput,
  getDefaultValues,
  getFormInputs,
} from './AddUserModal.forms';
import { AddUserFormData, AddUserModalProps } from './types';
import { useUserModalOptions } from './useUserModalOptions';

export function AddUserModal(props: AddUserModalProps): ReactElement {
  const {
    isOpen,
    closeModal,
    onConfirm,
    refetchQueries,
    userRoleType,
    userRoles,
    jobsite,
    contractor,
  } = props;

  const isMounted = useIsMounted();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [createUser] = useUserCreateMutation({ refetchQueries });
  const userRoleTypeLabel =
    userRoleType === UserAssignmentType.Developer ? 'Client' : userRoleType;

  const onSubmit: FormOnSubmit<AddUserFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      if (isEmpty(dirtyFields)) return;
      const input = getAddInput(userRoleType, data, dirtyFields);
      if (input) {
        await createUser({ variables: { input } });
        AlertService.alert('success', 'Success', 'User added');
        onConfirm();
      }
    } catch (error) {
      event.preventDefault();

      const emailAlreadyExistsError =
        getGraphQLErrorByCode<EmailAlreadyExistsErrorExtensions>(
          error,
          UserErrorCode.EmailAlreadyExists,
        );
      if (emailAlreadyExistsError) {
        formApi.setError('email', {
          message: (
            <AlreadyExistingItem
              itemType="Email"
              workerId={emailAlreadyExistsError.extensions.workerId}
            />
          ),
          shouldFocus: true,
        });
        return;
      }
      const phoneAlreadyExistsError =
        getGraphQLErrorByCode<PhoneAlreadyExistsErrorExtensions>(
          error,
          UserErrorCode.PhoneAlreadyExists,
        );
      if (phoneAlreadyExistsError) {
        formApi.setError('phoneNumber', {
          message: (
            <AlreadyExistingItem
              itemType="Phone"
              workerId={phoneAlreadyExistsError.extensions.workerId}
            />
          ),
          shouldFocus: true,
        });
        return;
      }
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const {
    userRolesOptions,
    contractorsOptions,
    developersOptions,
    jobsitesOptions,
    error,
  } = useUserModalOptions({
    editType: 'create',
    jobsite,
    contractor,
    userRoleTypes: [userRoleType],
    userRoles,
    includeAssignedObjects: true,
  });

  const formInputs = React.useMemo(
    () =>
      getFormInputs({
        userRoleType,
        userRolesOptions,
        contractorsOptions,
        jobsitesOptions,
        developersOptions,
      }),
    [userRoleType, userRolesOptions, contractorsOptions, jobsitesOptions],
  );
  const defaultValues = React.useMemo(
    () =>
      getDefaultValues({
        userRolesOptions,
        contractorsOptions,
        jobsitesOptions,
        developersOptions,
      }),
    [userRolesOptions, contractorsOptions, jobsitesOptions],
  );

  return (
    <ModalForm
      open={isOpen}
      setOpen={closeModal}
      title={`Add ${userRoleTypeLabel} User`}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Add User"
      actionIcon={UserPlusIcon}
      actionButtonWithSpinner={isSaving}
      error={error}
    />
  );
}
